/**
 * canvas-engine/renderer/elements/TextElement.jsx
 *
 * OWN per-type element renderer for `text` elements.
 *
 * ZERO REUSE: imports NOTHING from `admin-console-ui/src/modules/template-builder/`
 * or `academy-ui/src/template/`. It may import its own canvas-engine siblings only.
 *
 * ── Inner-content vs positioning CONTRACT (shared by every element renderer) ──
 * Each element component renders ONLY its inner content and fills its positioned
 * box (width:100% / height:100% of the wrapper). The parent `CanvasRenderer`
 * (Task 6.2) owns the absolute positioning frame (left/top/width/height/zIndex)
 * and renders this component inside that wrapper. Element components do NOT set
 * position/left/top/zIndex themselves.
 *
 * PARITY: DOM output is IDENTICAL regardless of `mode` ('builder' | 'reader').
 * Builder-only affordances (selection outlines, handles, toolbars) are added by
 * the editing layer / renderer wrapper, never here. (Req 20.4–20.7)
 *
 * Pure / presentational: no data fetching, no side effects. Resilient to missing
 * fields (the model normalizes, but we guard anyway). (Req 4.1, 13.1)
 */

import React from 'react';

/** Coerce to a renderable string, '' for nullish. */
function str(v) {
  return v == null ? '' : String(v);
}

/** Return a CSS color string when non-empty, else the fallback. */
function color(v, fallback) {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

/** Return a finite number (optionally with a px unit), else fallback. */
function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

/**
 * Render an eyebrow / heading / body text block.
 *
 * @param {object} props
 * @param {object} props.element normalized `text` Canvas_Element
 * @param {string} [props.mode] 'builder' | 'reader' (does NOT change DOM)
 */
export default function TextElement({ element }) {
  const content = (element && element.content) || {};
  const style = (element && element.style) || {};

  const eyebrow = str(content.eyebrow);
  const heading = str(content.heading);
  const body = str(content.body);

  const textAlign = style.textAlign === 'center' || style.textAlign === 'right' ? style.textAlign : 'left';
  const headingSize = num(style.headingSize, 32);
  const fontWeight = num(style.fontWeight, 600);

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 8,
    textAlign,
  };

  return (
    <div className="ce-text" style={rootStyle}>
      {eyebrow ? (
        <span
          className="ce-text__eyebrow"
          style={{
            color: color(style.eyebrowColor, '#2563eb'),
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          {eyebrow}
        </span>
      ) : null}

      {heading ? (
        <span
          className="ce-text__heading"
          style={{
            color: color(style.headingColor, '#0f172a'),
            fontSize: headingSize,
            fontWeight,
            lineHeight: 1.2,
            margin: 0,
          }}
        >
          {heading}
        </span>
      ) : null}

      {body ? (
        <span
          className="ce-text__body"
          style={{
            color: color(style.bodyColor, '#334155'),
            fontSize: num(style.bodySize, 16),
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}
        >
          {body}
        </span>
      ) : null}
    </div>
  );
}
